import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgManager } from '../lib/orgRoles';

interface OrgMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface ScheduleDetail {
  id: string;
  orgId: string;
  hostUserId: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  visibility: string;
  pitchStyle: string;
  mode: string;
  title: string | null;
  status: string;
  inviteEmailsSentAt?: string | null;
  host: { id: string; name: string | null; email: string };
  goal: { id: string; label: string } | null;
  schedulePersonas: { persona: { id: string; name: string; jobTitle: string } }[];
  session: { id: string; status: string } | null;
}

function scheduleInviteSkipStorageKey(scheduleId: string) {
  return `practiceScheduleInviteSkip:${scheduleId}`;
}

export function PracticeScheduleLobbyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const [schedule, setSchedule] = useState<ScheduleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [inviteUserIds, setInviteUserIds] = useState<string[]>([]);
  const [sendInviteToSelf, setSendInviteToSelf] = useState(true);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/practice/schedules/${id}`);
      setSchedule(res.data as ScheduleDetail);
    } catch (err) {
      setError(getApiErrorMessage(err));
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!id) return;
    const st = location.state as
      | { inviteError?: string; justCreatedWithInvitesSent?: boolean }
      | null
      | undefined;
    if (st?.justCreatedWithInvitesSent) {
      try {
        sessionStorage.setItem(scheduleInviteSkipStorageKey(id), '1');
      } catch {
        // ignore quota / private mode
      }
      navigate('.', { replace: true, state: null });
    }
    if (st?.inviteError) {
      setInviteError(st.inviteError);
      navigate('.', { replace: true, state: null });
    }
  }, [id, location.state, navigate]);

  const copyLobbyLink = useCallback(async () => {
    if (!id) return;
    const url = `${window.location.origin}/practice/schedule/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [id]);

  const isHost = user?.id === schedule?.hostUserId;
  const canManageAsOrgManager = isOrgManager(user?.orgRole);
  const canSendInvites = isHost || canManageAsOrgManager;
  const started = schedule?.status === 'started' && schedule.session;

  const scheduleInviteSkipFlag =
    id && typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(scheduleInviteSkipStorageKey(id)) === '1'
      : false;
  const invitesAlreadySent = Boolean(schedule?.inviteEmailsSentAt) || scheduleInviteSkipFlag;

  useEffect(() => {
    if (!id || !canSendInvites || started || schedule?.status !== 'scheduled') return;
    let cancelled = false;
    void api
      .get<OrgMember[]>('/orgs/me/members')
      .then((res) => {
        if (!cancelled) setOrgMembers(res.data);
      })
      .catch(() => {
        if (!cancelled) setOrgMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, canSendInvites, started, schedule?.status]);

  const inviteableMembers = orgMembers.filter((m) => m.id !== user?.id);

  const toggleInviteMember = (memberId: string) => {
    setInviteUserIds((prev) =>
      prev.includes(memberId) ? prev.filter((x) => x !== memberId) : [...prev, memberId],
    );
  };

  const handleSendInvites = async () => {
    if (!id) return;
    if (inviteUserIds.length === 0 && !sendInviteToSelf) {
      setInviteError('Select at least one teammate or send to yourself.');
      return;
    }
    setInviteSending(true);
    setInviteError(null);
    setInviteMessage(null);
    try {
      const res = await api.post(`/practice/schedules/${id}/invite-email`, {
        userIds: inviteUserIds,
        includeSelf: sendInviteToSelf,
      });
      setInviteMessage(`Sent ${(res.data as { sent: number }).sent} invite(s).`);
    } catch (err) {
      setInviteError(getApiErrorMessage(err));
      Sentry.captureException(err);
    } finally {
      setInviteSending(false);
    }
  };

  const handleStart = async () => {
    if (!id) return;
    setStarting(true);
    setError(null);
    try {
      const res = await api.post(`/practice/schedules/${id}/start`);
      navigate(`/practice/${res.data.id}`);
    } catch (err) {
      setError(getApiErrorMessage(err));
      Sentry.captureException(err);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return <p className="text-gray-600">Loading schedule…</p>;
  }

  if (error || !schedule) {
    return (
      <div className="max-w-xl mx-auto">
        <p
          className="text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {error ?? 'Schedule not found'}
        </p>
        <Link to="/practice" className="mt-4 inline-block text-green-700 hover:underline">
          Back to practice
        </Link>
      </div>
    );
  }

  const fmt = (iso: string) =>
    new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));

  return (
    <div className="max-w-2xl mx-auto">
      {inviteError && (
        <p
          className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {inviteError}
        </p>
      )}
      {inviteMessage && (
        <p className="mb-4 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          {inviteMessage}
        </p>
      )}
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        {schedule.title || 'Scheduled practice'}
      </h1>
      <p className="text-gray-600 mb-6">
        {fmt(schedule.scheduledStartAt)} – {fmt(schedule.scheduledEndAt)}
      </p>

      <div className="rounded-lg border border-gray-200 p-4 mb-6 space-y-2 text-sm">
        <p>
          <span className="text-gray-500">Host:</span>{' '}
          <span className="text-gray-900">{schedule.host.name || schedule.host.email}</span>
        </p>
        {schedule.goal && (
          <p>
            <span className="text-gray-500">Goal:</span>{' '}
            <span className="text-gray-900">{schedule.goal.label}</span>
          </p>
        )}
        <p>
          <span className="text-gray-500">Personas:</span>{' '}
          {schedule.schedulePersonas.map((sp) => sp.persona.name).join(', ')}
        </p>
        <p>
          <span className="text-gray-500">Visibility:</span>{' '}
          {schedule.visibility === 'org_open'
            ? 'Organization — teammates can join when live'
            : 'Private'}
        </p>
      </div>

      {started && (
        <div className="mb-6">
          <Link
            to={`/practice/${schedule.session!.id}`}
            className="inline-block bg-green-700 text-white px-4 py-2 rounded-md font-medium hover:bg-green-800"
          >
            Open practice session
          </Link>
        </div>
      )}

      {!started && schedule.status === 'scheduled' && (
        <>
          {isHost && (
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                type="button"
                onClick={() => void handleStart()}
                disabled={starting}
                className="bg-green-700 text-white px-6 py-2 rounded-md font-medium hover:bg-green-800 disabled:opacity-50"
              >
                {starting ? 'Starting…' : 'Start practice now'}
              </button>
              <button
                type="button"
                onClick={() => void copyLobbyLink()}
                className="border border-gray-300 text-gray-800 px-4 py-2 rounded-md font-medium hover:bg-gray-50"
              >
                {linkCopied ? 'Link copied' : 'Copy lobby link'}
              </button>
            </div>
          )}
          {canSendInvites && invitesAlreadySent && (
            <div className="rounded-lg border border-gray-200 p-4 mb-6">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Resend email invites</h2>
              <p className="text-sm text-gray-500 mb-3">
                Invites were already sent when this practice was created (or on a previous resend).
                Use this only if someone did not receive the message. Resending may create duplicate
                calendar entries for recipients. Your account is not listed below; check &quot;Send
                invite to myself&quot; if you want another copy.
              </p>
              {inviteableMembers.length === 0 ? (
                <p className="text-sm text-gray-500 mb-3">No other members in this organization.</p>
              ) : (
                <ul className="space-y-2 max-h-40 overflow-y-auto mb-3">
                  {inviteableMembers.map((m) => (
                    <li key={m.id}>
                      <label className="flex items-start gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                          checked={inviteUserIds.includes(m.id)}
                          onChange={() => toggleInviteMember(m.id)}
                        />
                        <span>
                          <span className="text-gray-900">{m.name || m.email}</span>
                          {m.name && <span className="block text-gray-500 text-xs">{m.email}</span>}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <label className="flex items-start gap-2 cursor-pointer text-sm mb-3">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                  checked={sendInviteToSelf}
                  onChange={(e) => setSendInviteToSelf(e.target.checked)}
                />
                <span className="text-gray-800">Send invite to myself</span>
              </label>
              <button
                type="button"
                onClick={() => void handleSendInvites()}
                disabled={inviteSending}
                className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
              >
                {inviteSending ? 'Sending…' : 'Resend email invites'}
              </button>
            </div>
          )}
          {canSendInvites && !invitesAlreadySent && (
            <div className="rounded-lg border border-gray-200 p-4 mb-6">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Email invites</h2>
              <p className="text-sm text-gray-500 mb-3">
                Send calendar invites with a 15-minute reminder. Your account is not listed below;
                check &quot;Send invite to myself&quot; if you want a copy.
              </p>
              {inviteableMembers.length === 0 ? (
                <p className="text-sm text-gray-500 mb-3">No other members in this organization.</p>
              ) : (
                <ul className="space-y-2 max-h-40 overflow-y-auto mb-3">
                  {inviteableMembers.map((m) => (
                    <li key={m.id}>
                      <label className="flex items-start gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                          checked={inviteUserIds.includes(m.id)}
                          onChange={() => toggleInviteMember(m.id)}
                        />
                        <span>
                          <span className="text-gray-900">{m.name || m.email}</span>
                          {m.name && <span className="block text-gray-500 text-xs">{m.email}</span>}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <label className="flex items-start gap-2 cursor-pointer text-sm mb-3">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                  checked={sendInviteToSelf}
                  onChange={(e) => setSendInviteToSelf(e.target.checked)}
                />
                <span className="text-gray-800">Send invite to myself</span>
              </label>
              <button
                type="button"
                onClick={() => void handleSendInvites()}
                disabled={inviteSending}
                className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
              >
                {inviteSending ? 'Sending…' : 'Send email invites'}
              </button>
            </div>
          )}
          {!isHost && (
            <p className="text-gray-600 mb-4">
              The host will start the practice. You can keep this page open or bookmark the lobby
              link.
            </p>
          )}
        </>
      )}

      {schedule.status === 'cancelled' && (
        <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          This schedule was cancelled.
        </p>
      )}

      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/practice/new"
          className="inline-block bg-green-700 text-white px-6 py-2 rounded-md font-medium hover:bg-green-800 transition-colors"
        >
          Start Another Practice
        </Link>
        <Link
          to="/"
          className="inline-block border border-gray-300 text-gray-800 px-6 py-2 rounded-md font-medium hover:bg-gray-50 transition-colors"
        >
          Return to Home
        </Link>
      </div>
    </div>
  );
}
